import "core-js/modules/es.array.includes.js";
import PropTypes from "prop-types";
import "core-js/modules/es.string.includes.js";
import "core-js/modules/es.array.sort.js";
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty(obj, key, value) {
  key = _toPropertyKey(key);
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _toPropertyKey(arg) {
  var key = _toPrimitive(arg, "string");
  return typeof key === "symbol" ? key : String(key);
}
function _toPrimitive(input, hint) {
  if (typeof input !== "object" || input === null) return input;
  var prim = input[Symbol.toPrimitive];
  if (prim !== undefined) {
    var res = prim.call(input, hint || "default");
    if (typeof res !== "object") return res;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return (hint === "string" ? String : Number)(input);
}
/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { Component } from 'react';
import { EuiBasicTable } from './basic_table';
import { isString } from '../../services/predicate';
import { Comparators } from '../../services/sort';
import { EuiSearchBar } from '../search_bar/search_bar';
import { EuiSearchBox } from '../search_bar/search_box';
import { EuiSpacer } from '../spacer';
import { euiTablePaginationDefaults } from '../table/table_pagination';
import { EuiComponentDefaultsContext } from '../provider/component_defaults';
function isEuiSearchBarProps(x) {
  return typeof x !== 'boolean';
}
var getQueryFromSearch = function getQueryFromSearch(search, defaultQuery) {
  var query;
  if (!search) {
    query = '';
  } else {
    query = (defaultQuery ? search.defaultQuery || search.query || '' : search.query) || '';
  }
  return isString(query) ? EuiSearchBar.Query.parse(query) : query;
};
var getInitialPagination = function getInitialPagination(pagination, consumerDefaults) {
  var _ref2, _pagination$pageIndex, _ref3, _pagination$pageSize;
  if (!pagination) {
    return {
      pageIndex: undefined,
      pageSize: undefined
    };
  }
  var defaults = _objectSpread(_objectSpread({}, euiTablePaginationDefaults), consumerDefaults);
  var _ref = pagination,
    _ref$pageSizeOptions = _ref.pageSizeOptions,
    pageSizeOptions = _ref$pageSizeOptions === void 0 ? defaults.itemsPerPageOptions : _ref$pageSizeOptions,
    _ref$showPerPageOptio = _ref.showPerPageOptions,
    showPerPageOptions = _ref$showPerPageOptio === void 0 ? defaults.showPerPageOptions : _ref$showPerPageOptio;
  var defaultPageSize = pageSizeOptions !== null && pageSizeOptions !== void 0 && pageSizeOptions.includes(defaults.itemsPerPage) ? defaults.itemsPerPage : pageSizeOptions[0];
  var initialPageIndex = pagination === true ? 0 : (_ref2 = (_pagination$pageIndex = pagination.pageIndex) !== null && _pagination$pageIndex !== void 0 ? _pagination$pageIndex : pagination.initialPageIndex) !== null && _ref2 !== void 0 ? _ref2 : 0;
  var initialPageSize = pagination === true ? defaultPageSize : (_ref3 = (_pagination$pageSize = pagination.pageSize) !== null && _pagination$pageSize !== void 0 ? _pagination$pageSize : pagination.initialPageSize) !== null && _ref3 !== void 0 ? _ref3 : defaultPageSize;
  if (showPerPageOptions && initialPageSize != null && !(pageSizeOptions !== null && pageSizeOptions !== void 0 && pageSizeOptions.includes(initialPageSize))) {
    throw new Error("EuiInMemoryTable received initialPageSize ".concat(initialPageSize, ", which wasn't provided within pageSizeOptions."));
  }
  return {
    pageIndex: initialPageIndex,
    pageSize: initialPageSize,
    pageSizeOptions: pageSizeOptions,
    showPerPageOptions: showPerPageOptions
  };
};
function findColumnByProp(columns, prop, value) {
  for (var i = 0; i < columns.length; i++) {
    var column = columns[i];
    if (column[prop] === value) {
      return column;
    }
  }
}
function findColumnByFieldOrName(columns, value) {
  // The passed value can be a column's `field` or its `name`
  // for backwards compatibility `field` must be checked first
  var column = findColumnByProp(columns, 'field', value);
  if (column == null) {
    column = findColumnByProp(columns, 'name', value);
  }
  return column;
}
function getInitialSorting(columns, sorting) {
  if (!sorting || !sorting.sort) {
    return {
      sortName: undefined,
      sortDirection: undefined
    };
  }
  var _sort = sorting.sort,
    sortable = _sort.field,
    sortDirection = _sort.direction;
  var sortColumn = findColumnByFieldOrName(columns, sortable);
  if (sortColumn == null) {
    return {
      sortName: undefined,
      sortDirection: undefined
    };
  }
  var sortName = sortColumn.name;
  return {
    sortName: sortName,
    sortDirection: sortDirection
  };
}
export class EuiInMemoryTable extends Component {
  static contextType = EuiComponentDefaultsContext;
  static defaultProps = {
    responsive: true,
    tableLayout: 'fixed',
    searchFormat: 'eql'
  };
  static getDerivedStateFromProps(nextProps, prevState) {
    let updatedPrevState = prevState;
    if (nextProps.items !== prevState.prevProps.items) {
      // We have new items because an external search has completed, so reset pagination state.

      let nextPageIndex = 0;
      if (nextProps.pagination != null && typeof nextProps.pagination !== 'boolean') {
        nextPageIndex = nextProps.pagination.pageIndex || 0;
      }
      updatedPrevState = {
        ...updatedPrevState,
        prevProps: {
          ...updatedPrevState.prevProps,
          items: nextProps.items
        },
        pageIndex: nextPageIndex
      };
    }

    // apply changes to controlled pagination
    if (nextProps.pagination != null && typeof nextProps.pagination !== 'boolean') {
      if (nextProps.pagination.pageSize != null && nextProps.pagination.pageSize !== updatedPrevState.pageIndex) {
        updatedPrevState = {
          ...updatedPrevState,
          pageSize: nextProps.pagination.pageSize
        };
      }
      if (nextProps.pagination.pageIndex != null && nextProps.pagination.pageIndex !== updatedPrevState.pageIndex) {
        updatedPrevState = {
          ...updatedPrevState,
          pageIndex: nextProps.pagination.pageIndex
        };
      }
    }
    const {
      sortName,
      sortDirection
    } = getInitialSorting(nextProps.columns, nextProps.sorting);
    if (sortName !== prevState.prevProps.sortName || sortDirection !== prevState.prevProps.sortDirection) {
      updatedPrevState = {
        ...updatedPrevState,
        prevProps: {
          ...updatedPrevState.prevProps,
          sortName,
          sortDirection
        },
        sortName,
        sortDirection
      };
    }
    const nextQuery = nextProps.search ? nextProps.search.query : '';
    const prevQuery = prevState.prevProps.search ? prevState.prevProps.search.query : '';
    if (nextQuery !== prevQuery) {
      updatedPrevState = {
        ...updatedPrevState,
        prevProps: {
          ...updatedPrevState.prevProps,
          search: nextProps.search
        },
        query: getQueryFromSearch(nextProps.search, false)
      };
    }
    if (updatedPrevState !== prevState) {
      return updatedPrevState;
    }
    return null;
  }
  constructor(props, context) {
    super(props);
    const {
      columns,
      search,
      pagination,
      sorting,
      allowNeutralSort
    } = props;
    const {
      pageIndex,
      pageSize,
      pageSizeOptions,
      showPerPageOptions
    } = getInitialPagination(pagination, context.EuiTablePagination);
    const {
      sortName,
      sortDirection
    } = getInitialSorting(columns, sorting);
    this.state = {
      prevProps: {
        items: props.items,
        sortName,
        sortDirection,
        search
      },
      search: search,
      query: getQueryFromSearch(search, true),
      pageIndex: pageIndex || 0,
      pageSize,
      pageSizeOptions,
      sortName,
      sortDirection,
      allowNeutralSort: allowNeutralSort !== false,
      showPerPageOptions
    };
  }
  onTableChange = ({
    page,
    sort
  }) => {
    let {
      index: pageIndex,
      size: pageSize
    } = page || {};

    // don't apply pagination changes that are otherwise controlled
    // `page` is left unchanged as it goes to the consumer's `onTableChange` callback, allowing the app to respond
    const {
      pagination
    } = this.props;
    if (pagination != null && typeof pagination !== 'boolean') {
      if (pagination.pageSize != null) pageSize = pagination.pageSize;
      if (pagination.pageIndex != null) pageIndex = pagination.pageIndex;
    }
    let {
      field: sortName,
      direction: sortDirection
    } = sort || {};

    // To keep backwards compatibility reportedSortName needs to be tracked separately
    // from sortName; sortName gets stored internally while reportedSortName is sent to the callback
    let reportedSortName = sortName;

    // EuiBasicTable returns the column's `field` instead of `name` on sort
    // and the column's `name` instead of `field` on pagination
    if (sortName) {
      const sortColumn = findColumnByFieldOrName(this.props.columns, sortName);
      if (sortColumn) {
        // Ensure sortName uses `name`
        sortName = sortColumn.name;

        // Ensure reportedSortName uses `field` if it exists
        const sortField = sortColumn.field;
        if (sortField) reportedSortName = sortField;
      }
    }

    // Allow going back to 'neutral' sorting
    if (this.state.allowNeutralSort && this.state.sortName === sortName && this.state.sortDirection === 'desc' && sortDirection === 'asc') {
      sortName = '';
      reportedSortName = '';
      sortDirection = 'asc'; // Default sort direction.
    }

    if (this.props.onTableChange) {
      this.props.onTableChange({
        // @ts-ignore complex relationship between pagination's existence and criteria, the code logic ensures this is correctly maintained
        page,
        sort: {
          field: reportedSortName,
          direction: sortDirection
        }
      });
    }
    this.setState({
      pageIndex,
      pageSize,
      sortName: sortName,
      sortDirection
    });
  };
  onQueryChange = ({
    query,
    queryText,
    error
  }) => {
    const {
      search
    } = this.props;
    if (isEuiSearchBarProps(search)) {
      if (search.onChange) {
        const shouldQueryInMemory = error == null ? search.onChange({
          query: query,
          queryText,
          error: null
        }) : search.onChange({
          query: null,
          queryText,
          error
        });
        if (!shouldQueryInMemory) {
          return;
        }
      }
    }

    // Reset pagination state.
    this.setState(state => ({
      prevProps: {
        ...state.prevProps,
        search
      },
      query,
      pageIndex: 0
    }));
  };

  // Alternative to onQueryChange - allows consumers to specify they want the
  // search bar to ignore EQL syntax and only use the searchbar for plain text
  onPlainTextSearch = searchValue => {
    const escapedQueryText = searchValue.replace(/["\\]/g, '\\$&');
    const finalQuery = `"${escapedQueryText}"`;
    this.setState({
      query: EuiSearchBar.Query.parse(finalQuery)
    });
  };
  renderSearchBar() {
    const {
      search,
      searchFormat
    } = this.props;
    if (!search) return;
    let searchBar;
    if (searchFormat === 'text') {
      const _searchBoxProps = search?.box || {}; // Work around | boolean type
      const {
        schema,
        ...searchBoxProps
      } = _searchBoxProps; // Destructure `schema` so it doesn't get rendered to DOM

      searchBar = <EuiSearchBox query="" // Unused, passed to satisfy Typescript
      {...searchBoxProps} onSearch={this.onPlainTextSearch} />;
    } else {
      let searchBarProps = {};
      if (isEuiSearchBarProps(search)) {
        const {
          onChange,
          ..._searchBarProps
        } = search;
        searchBarProps = _searchBarProps;
        if (searchBarProps.box && searchBarProps.box.schema === true) {
          searchBarProps.box = {
            ...searchBarProps.box,
            schema: this.resolveSearchSchema()
          };
        }
      }
      searchBar = <EuiSearchBar onChange={this.onQueryChange} {...searchBarProps} />;
    }
    return <>
        {searchBar}
        <EuiSpacer size="l" />
      </>;
  }
  resolveSearchSchema() {
    const {
      columns
    } = this.props;
    return columns.reduce((schema, column) => {
      const {
        field,
        dataType
      } = column;
      if (field) {
        const type = dataType || 'string';
        schema.fields[field] = {
          type
        };
      }
      return schema;
    }, {
      strict: true,
      fields: {}
    });
  }
  getItemSorter() {
    const {
      sortName,
      sortDirection
    } = this.state;
    const {
      columns
    } = this.props;
    const sortColumn = columns.find(({
      name
    }) => name === sortName);
    if (sortColumn == null) {
      // can't return a non-function so return a function that says everything is the same
      return () => 0;
    }
    const sortable = sortColumn.sortable;
    if (typeof sortable === 'function') {
      return Comparators.value(sortable, Comparators.default(sortDirection));
    }
    return Comparators.property(sortColumn.field, Comparators.default(sortDirection));
  }
  getItems() {
    const {
      executeQueryOptions
    } = this.props;
    const {
      prevProps: {
        items
      }
    } = this.state;
    if (!items.length) {
      return {
        items: [],
        totalItemCount: 0
      };
    }
    const {
      query,
      sortName,
      pageIndex,
      pageSize
    } = this.state;
    const matchingItems = query !== null && executeQueryOptions?.enabled !== false ? EuiSearchBar.Query.execute(query, items, executeQueryOptions) : items;
    const sortedItems = sortName ? matchingItems.slice(0) // avoid mutating the source array
    .sort(this.getItemSorter()) // sort, causes mutation
    : matchingItems;
    const visibleItems = pageSize && this.props.pagination ? (() => {
      const startIndex = pageIndex * pageSize;
      return sortedItems.slice(startIndex, Math.min(startIndex + pageSize, sortedItems.length));
    })() : sortedItems;
    return {
      items: visibleItems,
      totalItemCount: matchingItems.length
    };
  }
  render() {
    const {
      columns,
      loading,
      message,
      error,
      selection,
      isSelectable,
      hasActions,
      compressed,
      pagination: hasPagination,
      sorting: hasSorting,
      itemIdToExpandedRowMap,
      itemId,
      rowProps,
      cellProps,
      tableLayout,
      items: _unuseditems,
      search,
      searchFormat,
      onTableChange,
      executeQueryOptions,
      allowNeutralSort,
      childrenBetween,
      ...rest
    } = this.props;
    const {
      pageIndex,
      pageSize,
      pageSizeOptions,
      sortName,
      sortDirection,
      showPerPageOptions
    } = this.state;
    const {
      items,
      totalItemCount
    } = this.getItems();
    const pagination = !hasPagination ? undefined : {
      pageIndex,
      pageSize: pageSize ?? 1,
      pageSizeOptions,
      totalItemCount,
      showPerPageOptions
    };

    // Data loaded from a server can have a default sort order which is meaningful to the
    // user, but can't be reproduced with client-side sort logic. So we allow the table to display
    // rows in the order in which they're initially loaded by providing an undefined sorting prop.
    const sorting = !hasSorting ? undefined : {
      sort: !sortName && !sortDirection ? undefined : {
        field: sortName,
        direction: sortDirection
      },
      allowNeutralSort: this.state.allowNeutralSort
    };
    const searchBar = this.renderSearchBar();
    const table =
    // @ts-ignore complex relationship between pagination's existence and criteria, the code logic ensures this is correctly maintained
    <EuiBasicTable items={items} itemId={itemId} rowProps={rowProps} cellProps={cellProps} columns={columns} pagination={pagination} sorting={sorting} selection={selection} isSelectable={isSelectable} hasActions={hasActions} onChange={this.onTableChange} error={error} loading={loading} noItemsMessage={message} tableLayout={tableLayout} compressed={compressed} itemIdToExpandedRowMap={itemIdToExpandedRowMap} {...rest} />;
    if (!searchBar) {
      return table;
    }
    return <div>
        {searchBar}
        {childrenBetween}
        {table}
      </div>;
  }
}
EuiInMemoryTable.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any,
  /**
     * Describes how to extract a unique ID from each item, used for selections & expanded rows
     */
  /**
     * Describes how to extract a unique ID from each item, used for selections & expanded rows
     */
  itemId: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.number.isRequired, PropTypes.func.isRequired]),
  /**
     * Row expansion uses the itemId prop to identify each row
     */
  /**
     * Row expansion uses the itemId prop to identify each row
     */
  itemIdToExpandedRowMap: PropTypes.shape({}),
  /**
     * A list of objects to appear in the table - an item per row
     */
  /**
     * A list of objects to appear in the table - an item per row
     */
  items: PropTypes.arrayOf(PropTypes.any.isRequired),
  /**
     * Applied to `EuiTableRowCell`
     */
  /**
     * Applied to `EuiTableRowCell`
     */
  cellProps: PropTypes.oneOfType([PropTypes.any.isRequired, PropTypes.func.isRequired]),
  /**
     * An array of one of the objects: #EuiTableFieldDataColumnType, #EuiTableComputedColumnType or #EuiTableActionsColumnType.
     */
  /**
     * An array of one of the objects: #EuiTableFieldDataColumnType, #EuiTableComputedColumnType or #EuiTableActionsColumnType.
     */
  columns: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.shape({
    /**
       * A field of the item (may be a nested field)
       */
    // type hack used for better autocomplete support
    // https://github.com/microsoft/TypeScript/issues/29729
    field: PropTypes.oneOfType([PropTypes.any.isRequired, PropTypes.any.isRequired]).isRequired,
    // supports outer.inner key paths
    /**
       * The display name of the column
       */
    name: PropTypes.node.isRequired,
    /**
       * A description of the column (will be presented as a title over the column header)
       */
    description: PropTypes.string,
    /**
       * Describes the data types of the displayed value (serves as a rendering hint for the table)
       */
    dataType: PropTypes.oneOf(["auto", "string", "number", "boolean", "date"]),
    /**
       * A CSS width property. Hints for the required width of the column (e.g. "30%", "100px", etc..)
       */
    width: PropTypes.string,
    /**
       * Defines whether the user can sort on this column. If a function is provided, this function returns the value to sort against
       */
    sortable: PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.func.isRequired]),
    isExpander: PropTypes.bool,
    /**
       * Creates a text wrapper around cell content that helps word break or truncate
       * long text correctly.
       */
    textOnly: PropTypes.bool,
    /**
       * Defines the horizontal alignment of the column
       */
    align: PropTypes.any,
    /**
       * Indicates whether this column should truncate overflowing text content.
       * - Set to `true` to enable single-line truncation.
       * - To enable multi-line truncation, use a configuration object with `lines`
       * set to a number of lines to truncate to.
       */
    truncateText: PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.shape({
      lines: PropTypes.number.isRequired
    }).isRequired]),
    mobileOptions: PropTypes.shape({
      render: PropTypes.func
    }),
    /**
       * Describe a custom renderer function for the content
       */
    render: PropTypes.func,
    /**
       * Content to display in the footer beneath this column
       */
    footer: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.element.isRequired, PropTypes.func.isRequired]),
    /**
       * Disables the user's ability to change the sort but still shows the current direction
       */
    readOnly: PropTypes.bool,
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any
  }).isRequired, PropTypes.shape({
    /**
       * A function that computes the value for each item and renders it
       */
    render: PropTypes.func.isRequired,
    /**
       * The display name of the column
       */
    name: PropTypes.node,
    /**
       * A description of the column (will be presented as a title over the column header
       */
    description: PropTypes.string,
    /**
       * If provided, allows this column to be sorted on. Must return the value to sort against.
       */
    sortable: PropTypes.func,
    /**
       * A CSS width property. Hints for the required width of the column
       */
    width: PropTypes.string,
    /**
       * Indicates whether this column should truncate its content when it doesn't fit
       */
    truncateText: PropTypes.bool,
    isExpander: PropTypes.bool,
    align: PropTypes.any,
    /**
       * Disables the user's ability to change the sort but still shows the current direction
       */
    readOnly: PropTypes.bool,
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any
  }).isRequired, PropTypes.shape({
    /**
       * An array of one of the objects: #DefaultItemAction or #CustomItemAction
       */
    actions: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.shape({
      /**
         * The type of action
         */
      type: PropTypes.oneOfType([PropTypes.oneOf(["button"]), PropTypes.oneOf(["icon"]).isRequired]),
      /**
         * Defines the color of the button
         */
      color: PropTypes.oneOfType([PropTypes.any.isRequired, PropTypes.func.isRequired]),
      /**
         * The display name of the action (will render as visible text if rendered within a collapsed menu)
         */
      /**
         * The display name of the action (will render as visible text if rendered within a collapsed menu)
         */
      name: PropTypes.oneOfType([PropTypes.node.isRequired, PropTypes.func.isRequired]).isRequired,
      /**
         * Describes the action (will render as tooltip content)
         */
      /**
         * Describes the action (will render as tooltip content)
         */
      description: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.func.isRequired]).isRequired,
      /**
         * A handler function to execute the action
         */
      /**
         * A handler function to execute the action
         */
      onClick: PropTypes.func,
      href: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.func.isRequired]),
      target: PropTypes.string,
      /**
         * A callback function that determines whether the action is available
         */
      /**
         * A callback function that determines whether the action is available
         */
      available: PropTypes.func,
      /**
         * A callback function that determines whether the action is enabled
         */
      /**
         * A callback function that determines whether the action is enabled
         */
      enabled: PropTypes.func,
      isPrimary: PropTypes.bool,
      "data-test-subj": PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.func.isRequired]),
      /**
         * Associates an icon with the button
         */
      icon: PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addDataApp", "advancedSettingsApp", "agentApp", "aggregate", "analyzeEvent", "annotation", "apmApp", "apmTrace", "appSearchApp", "apps", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "arrowStart", "arrowEnd", "article", "asterisk", "at", "auditbeatApp", "beaker", "bell", "bellSlash", "beta", "bolt", "boxesHorizontal", "boxesVertical", "branch", "branchUser", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "casesApp", "check", "checkInCircleFilled", "cheer", "classificationJob", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "cluster", "codeApp", "color", "compute", "console", "consoleApp", "container", "continuityAbove", "continuityAboveBelow", "continuityBelow", "continuityWithin", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crosshairs", "currency", "cut", "dashboardApp", "dataVisualizer", "database", "desktop", "devToolsApp", "diff", "discoverApp", "discuss", "document", "documentEdit", "documentation", "documents", "dot", "dotInCircle", "doubleArrowLeft", "doubleArrowRight", "download", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorChecklist", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignLeft", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "endpoint", "eql", "eraser", "error", "esqlVis", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "filterExclude", "filterIgnore", "filterInclude", "filterInCircle", "flag", "fleetApp", "fold", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "frameNext", "framePrevious", "fullScreen", "fullScreenExit", "function", "gear", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "grabOmnidirectional", "gradient", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "home", "iInCircle", "image", "importAction", "index", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexRuntime", "indexSettings", "indexTemporary", "infinity", "inputOutput", "inspect", "invert", "ip", "key", "keyboard", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "kubernetesNode", "kubernetesPod", "launch", "layers", "lensApp", "lettering", "lineDashed", "lineDotted", "lineSolid", "link", "list", "listAdd", "lock", "lockOpen", "logoAWS", "logoAWSMono", "logoAerospike", "logoApache", "logoAppSearch", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticStack", "logoElasticsearch", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoVulnerabilityManagement", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logsApp", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithExclamation", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuDown", "menuLeft", "menuRight", "menuUp", "merge", "metricbeatApp", "metricsApp", "minimize", "minus", "minusInCircle", "minusInCircleFilled", "mobile", "monitoringApp", "moon", "namespace", "nested", "newChat", "node", "notebookApp", "number", "offline", "online", "outlierDetectionJob", "package", "packetbeatApp", "pageSelect", "pagesSelect", "palette", "paperClip", "partial", "pause", "payment", "pencil", "percent", "pin", "pinFilled", "pipeBreaks", "pipelineApp", "pipeNoBreaks", "pivot", "play", "playFilled", "plus", "plusInCircle", "plusInCircleFilled", "popout", "push", "questionInCircle", "quote", "recentlyViewedApp", "refresh", "regressionJob", "reporter", "reportingApp", "returnKey", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "sessionViewer", "shard", "share", "snowflake", "sortAscending", "sortDescending", "sortDown", "sortLeft", "sortRight", "sortUp", "sortable", "spaces", "spacesApp", "sparkles", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "sun", "swatchInput", "symlink", "tableDensityCompact", "tableDensityExpanded", "tableDensityNormal", "tableOfContents", "tag", "tear", "temperature", "timeline", "timelineWithArrow", "timelionApp", "timeRefresh", "timeslider", "training", "transitionLeftIn", "transitionLeftOut", "transitionTopIn", "transitionTopOut", "trash", "unfold", "unlink", "upgradeAssistantApp", "uptimeApp", "user", "userAvatar", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visTable", "visTagCloud", "visText", "visTimelion", "visVega", "visVisualBuilder", "visualizeApp", "vulnerabilityManagementApp", "warning", "alert", "watchesApp", "wordWrap", "wordWrapDisabled", "workplaceSearchApp", "wrench", "tokenAlias", "tokenAnnotation", "tokenArray", "tokenBinary", "tokenBoolean", "tokenClass", "tokenCompletionSuggester", "tokenConstant", "tokenDate", "tokenElement", "tokenEnum", "tokenEnumMember", "tokenEvent", "tokenException", "tokenField", "tokenFile", "tokenFlattened", "tokenFunction", "tokenGeo", "tokenHistogram", "tokenInterface", "tokenIP", "tokenJoin", "tokenKey", "tokenKeyword", "tokenMethod", "tokenMetricCounter", "tokenMetricGauge", "tokenModule", "tokenNamespace", "tokenNested", "tokenNull", "tokenNumber", "tokenObject", "tokenOperator", "tokenPackage", "tokenParameter", "tokenPercolator", "tokenProperty", "tokenRange", "tokenRankFeature", "tokenRankFeatures", "tokenRepo", "tokenSearchType", "tokenShape", "tokenString", "tokenStruct", "tokenSymbol", "tokenTag", "tokenText", "tokenTokenCount", "tokenVariable", "tokenVectorDense", "tokenDenseVector", "tokenVectorSparse"]).isRequired, PropTypes.func.isRequired])
    }).isRequired, PropTypes.shape({
      /**
         * Allows rendering a totally custom action
         */
      render: PropTypes.func.isRequired,
      /**
         * A callback that defines whether the action is available
         */
      available: PropTypes.func,
      /**
         * A callback that defines whether the action is enabled
         */
      enabled: PropTypes.func,
      isPrimary: PropTypes.bool
    }).isRequired]).isRequired).isRequired,
    /**
       * The display name of the column
       */
    name: PropTypes.node,
    /**
       * A description of the column (will be presented as a title over the column header
       */
    description: PropTypes.string,
    /**
       * A CSS width property. Hints for the required width of the column
       */
    width: PropTypes.string
  }).isRequired]).isRequired),
  /**
     * Error message to display
     */
  /**
     * Error message to display
     */
  error: PropTypes.string,
  /**
     * Describes the content of the table. If not specified, the caption will be "This table contains {itemCount} rows."
     */
  /**
     * Describes the content of the table. If not specified, the caption will be "This table contains {itemCount} rows."
     */
  tableCaption: PropTypes.string,
  /**
     * Indicates which column should be used as the identifying cell in each row. Should match a "field" prop in FieldDataColumn
     */
  /**
     * Indicates which column should be used as the identifying cell in each row. Should match a "field" prop in FieldDataColumn
     */
  rowHeader: PropTypes.string,
  hasActions: PropTypes.bool,
  isExpandable: PropTypes.bool,
  isSelectable: PropTypes.bool,
  /**
     * Provides an infinite loading indicator
     */
  /**
     * Provides an infinite loading indicator
     */
  loading: PropTypes.bool,
  /**
     * If true, will convert table to cards in mobile view
     */
  /**
     * If true, will convert table to cards in mobile view
     */
  responsive: PropTypes.bool,
  /**
     * Applied to `EuiTableRow`
     */
  /**
     * Applied to `EuiTableRow`
     */
  rowProps: PropTypes.oneOfType([PropTypes.any.isRequired, PropTypes.func.isRequired]),
  /**
     * Configures #EuiTableSelectionType
     */
  /**
     * Configures #EuiTableSelectionType
     */
  selection: PropTypes.shape({
    /**
       * A callback that will be called whenever the item selection changes.
       *
       * Required if `selected` is passed.
       */
    onSelectionChange: PropTypes.func,
    /**
       * A callback that is called per item to indicate whether it is selectable
       */
    selectable: PropTypes.func,
    /**
       * A callback that is called per item to retrieve a message for its selectable state.
       * We display these messages as a tooltip on an unselectable checkbox
       */
    selectableMessage: PropTypes.func,
    /**
       * Sets initially selected items. Use for uncontrolled selection behavior (checkbox
       * will only change from user input, and not from developer control).
       *
       * This prop will be ignored if `selected` is passed.
       */
    initialSelected: PropTypes.arrayOf(PropTypes.any.isRequired),
    /**
       * Used for controlled selection behavior, e.g. when you want to programmatically
       * control which selection checkboxes are checked, and which are not.
       */
    selected: PropTypes.arrayOf(PropTypes.any.isRequired)
  }),
  /**
     * Sets the table-layout CSS property. Note that auto tableLayout prevents truncateText from working properly.
     */
  /**
     * Sets the table-layout CSS property. Note that auto tableLayout prevents truncateText from working properly.
     */
  tableLayout: PropTypes.oneOf(["fixed", "auto"]),
  /**
     * Applied to table cells. Any cell using a render function will set this to be false.
     *
     * Creates a text wrapper around cell content that helps word break or truncate
     * long text correctly.
     */
  /**
     * Applied to table cells. Any cell using a render function will set this to be false.
     *
     * Creates a text wrapper around cell content that helps word break or truncate
     * long text correctly.
     */
  textOnly: PropTypes.bool,
  message: PropTypes.node,
  /**
     * Configures #Search.
     */
  /**
     * Configures #Search.
     */
  search: PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.shape({
    onChange: PropTypes.func,
    /**
       The initial query the bar will hold when first mounted
       */
    defaultQuery: PropTypes.oneOfType([PropTypes.any.isRequired, PropTypes.string.isRequired]),
    /**
       If you wish to use the search bar as a controlled component, continuously pass the query via this prop.
       */
    query: PropTypes.oneOfType([PropTypes.any.isRequired, PropTypes.string.isRequired]),
    /**
       Configures the search box. Set `placeholder` to change the placeholder text in the box and `incremental` to support incremental (as you type) search.
       */
    box: PropTypes.shape({
      name: PropTypes.string,
      id: PropTypes.string,
      placeholder: PropTypes.string,
      value: PropTypes.string,
      isInvalid: PropTypes.bool,
      /**
         * Expand to fill 100% of the parent.
         * Defaults to `fullWidth` prop of `<EuiForm>`.
         * @default false
         */
      fullWidth: PropTypes.bool,
      isLoading: PropTypes.bool,
      /**
         * Called when the user presses [Enter] OR on change if the incremental prop is `true`.
         * If you don't need the on[Enter] functionality, prefer using onChange
         */
      onSearch: PropTypes.func,
      /**
         * When `true` the search will be executed (that is, the `onSearch` will be called) as the
         * user types.
         */
      incremental: PropTypes.bool,
      /**
         * when `true` creates a shorter height input
         */
      compressed: PropTypes.bool,
      inputRef: PropTypes.func,
      /**
         * Shows a button that quickly clears any input
         */
      isClearable: PropTypes.bool,
      /**
         * Creates an input group with element(s) coming before input
         * `string` | `ReactElement` or an array of these
         */
      prepend: PropTypes.oneOfType([PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.element.isRequired]).isRequired, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.element.isRequired]).isRequired).isRequired]),
      /**
         * Creates an input group with element(s) coming after input.
         * `string` | `ReactElement` or an array of these
         */
      append: PropTypes.oneOfType([PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.element.isRequired]).isRequired, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.element.isRequired]).isRequired).isRequired]),
      className: PropTypes.string,
      "aria-label": PropTypes.string,
      "data-test-subj": PropTypes.string,
      css: PropTypes.any,
      // Boolean values are not meaningful to this EuiSearchBox, but are allowed so that other
      // components can use e.g. a true value to mean "auto-derive a schema". See EuiInMemoryTable.
      // Admittedly, this is a bit of a hack.
      schema: PropTypes.oneOfType([PropTypes.shape({
        strict: PropTypes.bool,
        fields: PropTypes.any,
        flags: PropTypes.arrayOf(PropTypes.string.isRequired)
      }).isRequired, PropTypes.bool.isRequired])
    }),
    /**
       An array of search filters. See #SearchFilterConfig.
       */
    filters: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.shape({
      type: PropTypes.oneOf(["is"]).isRequired,
      field: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      negatedName: PropTypes.string,
      available: PropTypes.func
    }).isRequired, PropTypes.shape({
      type: PropTypes.oneOf(["field_value_selection"]).isRequired,
      field: PropTypes.string,
      name: PropTypes.string.isRequired,
      /**
         * See #FieldValueOptionType
         */
      options: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape({
        field: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.number.isRequired, PropTypes.bool.isRequired, PropTypes.shape({
          type: PropTypes.oneOf(["date"]).isRequired,
          raw: PropTypes.any.isRequired,
          granularity: PropTypes.oneOfType([PropTypes.shape({
            es: PropTypes.oneOf(["d", "w", "M", "y"]).isRequired,
            js: PropTypes.oneOf(["day", "week", "month", "year"]).isRequired,
            isSame: PropTypes.func.isRequired,
            start: PropTypes.func.isRequired,
            startOfNext: PropTypes.func.isRequired,
            iso8601: PropTypes.func.isRequired
          }).isRequired, PropTypes.oneOf([undefined])]).isRequired,
          text: PropTypes.string.isRequired,
          resolve: PropTypes.func.isRequired
        }).isRequired]).isRequired,
        name: PropTypes.string,
        view: PropTypes.node
      }).isRequired).isRequired, PropTypes.func.isRequired]).isRequired,
      filterWith: PropTypes.oneOfType([PropTypes.oneOf(["prefix", "includes"]), PropTypes.func.isRequired]),
      cache: PropTypes.number,
      multiSelect: PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.oneOf(["and", "or"])]),
      loadingMessage: PropTypes.string,
      noOptionsMessage: PropTypes.string,
      searchThreshold: PropTypes.number,
      available: PropTypes.func,
      autoClose: PropTypes.bool,
      operator: PropTypes.oneOf(["eq", "exact", "gt", "gte", "lt", "lte"])
    }).isRequired, PropTypes.shape({
      type: PropTypes.oneOf(["field_value_toggle"]).isRequired,
      field: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.number.isRequired, PropTypes.bool.isRequired, PropTypes.shape({
        type: PropTypes.oneOf(["date"]).isRequired,
        raw: PropTypes.any.isRequired,
        granularity: PropTypes.oneOfType([PropTypes.shape({
          es: PropTypes.oneOf(["d", "w", "M", "y"]).isRequired,
          js: PropTypes.oneOf(["day", "week", "month", "year"]).isRequired,
          isSame: PropTypes.func.isRequired,
          start: PropTypes.func.isRequired,
          startOfNext: PropTypes.func.isRequired,
          iso8601: PropTypes.func.isRequired
        }).isRequired, PropTypes.oneOf([undefined])]).isRequired,
        text: PropTypes.string.isRequired,
        resolve: PropTypes.func.isRequired
      }).isRequired]).isRequired,
      name: PropTypes.string.isRequired,
      negatedName: PropTypes.string,
      available: PropTypes.func,
      operator: PropTypes.oneOf(["eq", "exact", "gt", "gte", "lt", "lte"])
    }).isRequired, PropTypes.shape({
      type: PropTypes.oneOf(["field_value_toggle_group"]).isRequired,
      field: PropTypes.string.isRequired,
      /**
         * See #FieldValueToggleGroupFilterItemType
         */
      items: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.number.isRequired, PropTypes.bool.isRequired]).isRequired,
        name: PropTypes.string.isRequired,
        negatedName: PropTypes.string,
        operator: PropTypes.oneOf(["eq", "exact", "gt", "gte", "lt", "lte"])
      }).isRequired).isRequired,
      available: PropTypes.func
    }).isRequired, PropTypes.shape({
      type: PropTypes.oneOf(["custom_component"]).isRequired,
      component: PropTypes.elementType.isRequired,
      available: PropTypes.func
    }).isRequired]).isRequired),
    /**
       * Tools which go to the left of the search bar.
       */
    toolsLeft: PropTypes.oneOfType([PropTypes.element.isRequired, PropTypes.arrayOf(PropTypes.element.isRequired).isRequired]),
    /**
       * Tools which go to the right of the search bar.
       */
    toolsRight: PropTypes.oneOfType([PropTypes.element.isRequired, PropTypes.arrayOf(PropTypes.element.isRequired).isRequired]),
    /**
       * Date formatter to use when parsing date values
       */
    dateFormat: PropTypes.any,
    /**
       * Hint to render below the search bar
       */
    hint: PropTypes.shape({
      content: PropTypes.node.isRequired,
      popoverProps: PropTypes.any
    }),
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any
  }).isRequired]),
  /**
     * By default, tables use `eql` format for search which allows using advanced filters.
     *
     * However, certain special characters (such as quotes, parentheses, and colons)
     * are reserved for EQL syntax and will error if used.
     * If your table does not require filter search and instead requires searching for certain
     * symbols, use a plain `text` search format instead (note that filters will be ignored
     * in this format).
     *
     * @default "eql"
     */
  /**
     * By default, tables use `eql` format for search which allows using advanced filters.
     *
     * However, certain special characters (such as quotes, parentheses, and colons)
     * are reserved for EQL syntax and will error if used.
     * If your table does not require filter search and instead requires searching for certain
     * symbols, use a plain `text` search format instead (note that filters will be ignored
     * in this format).
     *
     * @default "eql"
     */
  searchFormat: PropTypes.oneOf(["eql", "text"]),
  /**
     * Configures #Pagination
     */
  pagination: PropTypes.oneOfType([PropTypes.oneOf([undefined]), PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.shape({
    pageSizeOptions: PropTypes.arrayOf(PropTypes.number.isRequired),
    initialPageIndex: PropTypes.number,
    initialPageSize: PropTypes.number,
    pageIndex: PropTypes.number,
    pageSize: PropTypes.number,
    /**
       * Option to completely hide the "Rows per page" selector.
       *
       * @default true
       */
    showPerPageOptions: PropTypes.bool,
    /**
       * Current selection for "Rows per page".
       * Pass `0` to display the selected "Show all" option and hide the pagination.
       *
       * @default 10
       */
    itemsPerPage: PropTypes.number,
    /**
       * Custom array of options for "Rows per page".
       * Pass `0` as one of the options to create a "Show all" option.
       *
       * @default [10, 25, 50]
       */
    itemsPerPageOptions: PropTypes.arrayOf(PropTypes.number.isRequired),
    /**
       * Click handler that passes back selected `pageSize` number
       */
    onChangeItemsPerPage: PropTypes.func,
    onChangePage: PropTypes.func,
    /**
       * Requires the `id` of the table being controlled
       */
    "aria-controls": PropTypes.string,
    "aria-label": PropTypes.string
  }).isRequired])]),
  /**
     * Configures #EuiTableSortingType
     */
  /**
     * Configures #EuiTableSortingType
     */
  sorting: PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.shape({
    sort: PropTypes.any.isRequired
  }).isRequired]),
  /**
     * Set `allowNeutralSort` to false to force column sorting. Defaults to true.
     */
  /**
     * Set `allowNeutralSort` to false to force column sorting. Defaults to true.
     */
  allowNeutralSort: PropTypes.bool,
  /**
     * `onChange` is not required when `pagination` and/or `sorting` are configured,
     * but if `onChange` is present it is responsible for handling state for each/both.
     * See #Criteria or #CriteriaWithPagination
     */
  /**
     * `onChange` is not required when `pagination` and/or `sorting` are configured,
     * but if `onChange` is present it is responsible for handling state for each/both.
     * See #Criteria or #CriteriaWithPagination
     */
  onChange: PropTypes.any,
  /**
     * Callback for when table pagination or sorting is changed. This is meant to be informational only, and not used to set any state as the in-memory table already manages this state. See #Criteria or #CriteriaWithPagination.
     */
  onTableChange: PropTypes.func,
  executeQueryOptions: PropTypes.shape({
    defaultFields: PropTypes.arrayOf(PropTypes.string.isRequired),
    isClauseMatcher: PropTypes.func,
    explain: PropTypes.bool,
    /**
         * When the search bar Query is controlled and passed to the `search` prop it is by default executed against the items passed to the table to filter them out.
         * If the filtering is already done before passing the `items` to the table we can disable the execution by setting `enabled` to `false`.
         */
    enabled: PropTypes.bool
  }),
  /**
     * Insert content between the search bar and table components.
     */
  /**
     * Insert content between the search bar and table components.
     */
  childrenBetween: PropTypes.node
};
try {
  EuiInMemoryTable.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/basic_table/in_memory_table.tsx',
    description: '',
    displayName: 'EuiInMemoryTable',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      error: {
        defaultValue: null,
        description: 'Error message to display',
        name: 'error',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      responsive: {
        defaultValue: {
          value: 'true'
        },
        description: 'If true, will convert table to cards in mobile view',
        name: 'responsive',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      compressed: {
        defaultValue: null,
        description: '',
        name: 'compressed',
        parent: {
          fileName: 'app/src/components/table/table.tsx',
          name: 'EuiTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/table/table.tsx',
          name: 'EuiTableProps'
        }, {
          fileName: 'app/src/components/table/table.tsx',
          name: 'EuiTableProps'
        }, {
          fileName: 'app/src/components/table/table.tsx',
          name: 'EuiTableProps'
        }, {
          fileName: 'app/src/components/table/table.tsx',
          name: 'EuiTableProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      items: {
        defaultValue: null,
        description: 'A list of objects to appear in the table - an item per row',
        name: 'items',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: true,
        type: {
          name: 'T[]'
        }
      },
      columns: {
        defaultValue: null,
        description: 'An array of one of the objects: #EuiTableFieldDataColumnType, #EuiTableComputedColumnType or #EuiTableActionsColumnType.',
        name: 'columns',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: true,
        type: {
          name: 'EuiBasicTableColumn<T>[]'
        }
      },
      loading: {
        defaultValue: null,
        description: 'Provides an infinite loading indicator',
        name: 'loading',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      tableLayout: {
        defaultValue: {
          value: 'fixed'
        },
        description: 'Sets the table-layout CSS property. Note that auto tableLayout prevents truncateText from working properly.',
        name: 'tableLayout',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"fixed" | "auto"',
          value: [{
            value: '"fixed"'
          }, {
            value: '"auto"'
          }]
        }
      },
      isSelectable: {
        defaultValue: null,
        description: '',
        name: 'isSelectable',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      hasActions: {
        defaultValue: null,
        description: '',
        name: 'hasActions',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isExpandable: {
        defaultValue: null,
        description: '',
        name: 'isExpandable',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      textOnly: {
        defaultValue: null,
        description: 'Applied to table cells. Any cell using a render function will set this to be false.\n' + '\n' + 'Creates a text wrapper around cell content that helps word break or truncate\n' + 'long text correctly.',
        name: 'textOnly',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      itemId: {
        defaultValue: null,
        description: 'Describes how to extract a unique ID from each item, used for selections & expanded rows',
        name: 'itemId',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: false,
        type: {
          name: 'ItemId<T>'
        }
      },
      itemIdToExpandedRowMap: {
        defaultValue: null,
        description: 'Row expansion uses the itemId prop to identify each row',
        name: 'itemIdToExpandedRowMap',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: false,
        type: {
          name: 'ItemIdToExpandedRowMap'
        }
      },
      cellProps: {
        defaultValue: null,
        description: 'Applied to `EuiTableRowCell`',
        name: 'cellProps',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: false,
        type: {
          name: 'object | CellPropsCallback<T>'
        }
      },
      tableCaption: {
        defaultValue: null,
        description: 'Describes the content of the table. If not specified, the caption will be "This table contains {itemCount} rows."',
        name: 'tableCaption',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      rowHeader: {
        defaultValue: null,
        description: 'Indicates which column should be used as the identifying cell in each row. Should match a "field" prop in FieldDataColumn',
        name: 'rowHeader',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      rowProps: {
        defaultValue: null,
        description: 'Applied to `EuiTableRow`',
        name: 'rowProps',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: false,
        type: {
          name: 'object | RowPropsCallback<T>'
        }
      },
      selection: {
        defaultValue: null,
        description: 'Configures #EuiTableSelectionType',
        name: 'selection',
        parent: {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        },
        declarations: [{
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }, {
          fileName: 'app/src/components/basic_table/basic_table.tsx',
          name: 'BasicTableProps'
        }],
        required: false,
        type: {
          name: 'EuiTableSelectionType<T>'
        }
      },
      message: {
        defaultValue: null,
        description: '',
        name: 'message',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/basic_table/in_memory_table.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/basic_table/in_memory_table.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      search: {
        defaultValue: null,
        description: 'Configures #Search.',
        name: 'search',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/basic_table/in_memory_table.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/basic_table/in_memory_table.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'Search'
        }
      },
      searchFormat: {
        defaultValue: {
          value: 'eql'
        },
        description: 'By default, tables use `eql` format for search which allows using advanced filters.\n' + '\n' + 'However, certain special characters (such as quotes, parentheses, and colons)\n' + 'are reserved for EQL syntax and will error if used.\n' + 'If your table does not require filter search and instead requires searching for certain\n' + 'symbols, use a plain `text` search format instead (note that filters will be ignored\n' + 'in this format).',
        name: 'searchFormat',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/basic_table/in_memory_table.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/basic_table/in_memory_table.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"text" | "eql"',
          value: [{
            value: '"text"'
          }, {
            value: '"eql"'
          }]
        }
      },
      pagination: {
        defaultValue: null,
        description: 'Configures #Pagination',
        name: 'pagination',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/basic_table/in_memory_table.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/basic_table/in_memory_table.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'Pagination'
        }
      },
      sorting: {
        defaultValue: null,
        description: 'Configures #EuiTableSortingType',
        name: 'sorting',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/basic_table/in_memory_table.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/basic_table/in_memory_table.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'Sorting'
        }
      },
      allowNeutralSort: {
        defaultValue: null,
        description: 'Set `allowNeutralSort` to false to force column sorting. Defaults to true.',
        name: 'allowNeutralSort',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/basic_table/in_memory_table.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/basic_table/in_memory_table.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      onChange: {
        defaultValue: null,
        description: '`onChange` is not required when `pagination` and/or `sorting` are configured,\n' + 'but if `onChange` is present it is responsible for handling state for each/both.\n' + 'See #Criteria or #CriteriaWithPagination',
        name: 'onChange',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/basic_table/in_memory_table.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/basic_table/in_memory_table.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '((criteria: Criteria<T>) => void) | ((criteria: CriteriaWithPagination<T>) => void)'
        }
      },
      onTableChange: {
        defaultValue: null,
        description: 'Callback for when table pagination or sorting is changed. This is meant to be informational only, and not used to set any state as the in-memory table already manages this state. See #Criteria or #CriteriaWithPagination.',
        name: 'onTableChange',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/basic_table/in_memory_table.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/basic_table/in_memory_table.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '((nextValues: Criteria<T>) => void) | ((nextValues: CriteriaWithPagination<T>) => void)'
        }
      },
      executeQueryOptions: {
        defaultValue: null,
        description: '',
        name: 'executeQueryOptions',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/basic_table/in_memory_table.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/basic_table/in_memory_table.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '{ defaultFields?: string[]; isClauseMatcher?: (...args: any) => boolean; explain?: boolean; enabled?: boolean; }'
        }
      },
      childrenBetween: {
        defaultValue: null,
        description: 'Insert content between the search bar and table components.',
        name: 'childrenBetween',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/basic_table/in_memory_table.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/basic_table/in_memory_table.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      }
    },
    extendedInterfaces: ['CommonProps', 'HTMLAttributes', 'TableHTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'BasicTableProps', 'EuiTableProps']
  };
} catch (e) {}